import type { FC } from 'react'
import { useLayoutEffect, useRef } from 'react'
import { Trans } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { IMPERSONATION_BANNER_HEIGHT_PROPERTY } from '@/constants/impersonationBanner'
import { useAuth } from '@/hooks/use-auth'
import useTranslate from '@/hooks/use-translate'
import { tokens } from '@/locales/tokens'
import { useImpersonationSession } from './use-impersonation-session'

export const ImpersonationBanner: FC = () => {
  const auth = useAuth()
  const { t: tCommon } = useTranslate('common')
  const session = useImpersonationSession()
  const banner = useRef<HTMLDivElement>(null)

  // Publiée pour les écrans en pleine hauteur, qui la retirent de la leur (`IMPERSONATION_BANNER_HEIGHT`).
  useLayoutEffect(() => {
    const element = banner.current
    if (!element) return
    const root = document.documentElement
    const publish = () =>
      root.style.setProperty(
        IMPERSONATION_BANNER_HEIGHT_PROPERTY,
        `${element.offsetHeight}px`
      )
    publish()
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    observer?.observe(element)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(IMPERSONATION_BANNER_HEIGHT_PROPERTY)
    }
  }, [auth.isImpersonating])

  if (!auth.isImpersonating) {
    return null
  }

  const name = [auth.user?.profile?.firstName, auth.user?.profile?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()
  const label = name || auth.user?.email || '—'

  return (
    <Alert
      ref={banner}
      severity="warning"
      data-testid="impersonation-banner"
      action={
        <Button
          color="inherit"
          size="small"
          data-testid="stop-impersonation-button"
          onClick={session.end}
        >
          {tCommon('stopImpersonation')}
        </Button>
      }
      sx={{ borderRadius: 0 }}
    >
      <Trans
        i18nKey={tokens.common.impersonatingAs}
        values={{ name: label }}
        components={{ bold: <strong /> }}
      />
      {session.minutesLeft !== null &&
        ` · ${tCommon('impersonationTimeLeft', {
          minutes: session.minutesLeft,
        })}`}
    </Alert>
  )
}
